import moment from 'moment'
import { omit, keyBy, values, isFunction, keys, difference } from 'lodash'
import { timestamp } from '../../../lib/Tools'

const _doc = collection => (state, action) => {
  if (action.type == `UPDATE_ONE_${collection}` || action.type == `UPDATE_MANY_${collection}`)
    return {
      ...state, 
      ...action.payload, 
      modification: timestamp()
    }
  else 
    return state
}
    
const _rehydrate = emptyDoc => x => ({ 
  ...(isFunction(emptyDoc) ? emptyDoc() : emptyDoc), 
  ...x 
})

/**
 * 
 */

export default function createReducer(
  collection: string, 
  emptyDoc: Object|() => Object, 
  customReducers: {[string]: (Object, Object) => Object}, 
  opt = { expunge: false, customRehydrate: null, idKey: "_id" }
) {
  const rehydrate = opt.customRehydrate || _rehydrate(emptyDoc)

  // @TODO overload single doc reducer?
  const doc = _doc(collection)

  return (state = {}, action) => {
    if (!state && action.type !== `RECEIVE_ALL_${collection}`)
      return state

    if (customReducers && customReducers[action.type]) {
      return customReducers[action.type](state, action)
    }

    switch (action.type) {
    case "SERVER_UPDATE":
      const lc = collection.toLowerCase()
      if (action.payload.c[lc] || action.payload.u[lc] || action.payload.d[lc]) {
        const updated = {}

        keys(action.payload.u[lc]).forEach(k => { 
          updated[k] = { ...state[k], ...action.payload.u[lc][k] }
        })

        return omit({
          ...state, 
          ...updated, 
          ...keyBy(action.payload.c[lc], "_id")
        }, action.payload.d[lc])
      }
      else return state
    
    case `CREATE_ONE_${collection}`:
      return { 
        ...state, 
        [action.id]: {
          ...(isFunction(emptyDoc) ? emptyDoc() : emptyDoc),  
          ...action.payload, 
          creation: timestamp(), 
          modification: timestamp(), 
          _id: action.id
        } 
      }

    case `REMOVE_ONE_${collection}`:
      return (opt.expunge
        ? omit(state, action.id)
        : {
          ...state, 
          [action.id]: {
            ...state[action.id], 
            deleted: moment().valueOf()
          }
        }
      )

    case `RECEIVE_ALL_${collection}`:
      if (action.error) {
        console.error(`Error receiving ${collection}: ${action.error}`)
        return state
      }
      if (!action.payload) {
        console.error(`action of type ${action.type} carries no payload`)
        console.error(action)
        return state
      }
      return keyBy(action.payload.filter(x => !!x).map(rehydrate), opt.idKey || "_id")

    case `RECEIVE_ONE_${collection}`:
      // console.log(`reducer for RECEIVE_ONE_${collection}, got ${JSON.stringify(action.payload)}`)
      return {
        ...state, 
        [action.id]: rehydrate(action.payload)
      }

    case `UPDATE_ONE_${collection}`:
      return {
        ...state, 
        [action.id]: doc(state[action.id], action)
      }

    case `UPDATE_MANY_${collection}`:
      const ids = new Set(action.id)
      return {
        ...state,
        ...keyBy(
          values(state).filter(x => ids.has(x._id)).map(x => doc(x, action)),
          "_id"
        )
      }

    default: 
      return state
    }
  }
}
